import { FaCrown, FaStore, FaIndustry } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";


// Each plan's "What's included" is a list of CATEGORY GROUPS, each holding chip items, instead of
// one long flat bullet list (redesign: /your-step compact cards + accordion, ticket parts 3-10).
//
// Item shape: { key, fallback, route?, featureKey? }
//   key         "p1".."p13" reuses the EXISTING translated text already in
//               planPrivileges.<planId>.<key> (menuElakay_*.json) -- never re-translated here.
//               Any other key (e.g. "ds1") is new content: userStep.js falls back to
//               elakayAutoText(`h_userstep_<planId>_<key>`, fallback) for it, same
//               try-existing-then-elakayAutoText pattern used elsewhere in this codebase.
//   route       optional real frontend route for the chip to link to (only set when a single,
//               real destination exists -- never invented).
//   featureKey  optional key from utils/sellCenterStatus.js's SELL_CENTER_FEATURE_KEYS -- only
//               set when that exact key already exists, so the chip can show a real
//               ACTIVE/SOON/COMING BACK badge via getSellCenterFeatureStatus(). Omitted entirely
//               for categories with no existing AnimationSettings entry (never a fabricated status).
export const PLANS = [
  {
    id: "royalty",
    eventKey: "0",
    roleName: "Royalty",
    roleIndex: 5,
    icon: FaCrown,
    accentColor: "#007399",
    priceMonth: 12,
    priceYear: 100,
    titleKey: "beRoyalty",
    taglineKey: "royaltyTagline",
    taglineFallback: "Shop & Save",
    descriptionKey: "royaltyDescription",
    descriptionFallback: "Shop, save, and enjoy exclusive Royalty perks across the Elakay marketplace.",
    available: true,
    prixAvailable: false,
    freeTrialMonths: 12,
    privilegeGroups: [
      {
        key: "shopping",
        labelFallback: "Shopping",
        items: [
          { key: "sh1", fallback: "Browse Marketplace", route: "/" },
          { key: "sh2", fallback: "Product Search", route: "/search" },
          { key: "sh3", fallback: "Categories" },
          { key: "sh4", fallback: "Product Preview" },
          { key: "sh5", fallback: "Product Filters" },
          { key: "sh6", fallback: "Shopping Cart", route: "/cart" },
          { key: "sh7", fallback: "Buy Now" },
          { key: "sh8", fallback: "My Orders", route: "/c/orders" },
        ],
      },
      {
        key: "discovery",
        labelFallback: "Discovery",
        items: [
          { key: "di1", fallback: "New Products" },
          { key: "di2", fallback: "Best Sellers", route: "/best-sellers" },
          { key: "di3", fallback: "Today's Deals", route: "/deal-today" },
          { key: "di4", fallback: "Search Results", route: "/search" },
          { key: "di5", fallback: "Shops & Sellers Discovery" },
        ],
      },
      {
        key: "account",
        labelFallback: "Account & Benefits",
        items: [
          { key: "p1", fallback: "Free shipping on eligible products" },
          { key: "p2", fallback: "Exclusive deals and private discounts" },
          { key: "p3", fallback: "Early access to new products and promotions" },
          { key: "p4", fallback: "Priority delivery (faster shipping)" },
          { key: "p5", fallback: "VIP customer support" },
          { key: "p6", fallback: "Access to premium brands and limited collections" },
          { key: "p7", fallback: "Personal rewards and cashback system" },
          { key: "p8", fallback: "Private account benefits (features reserved only for Royalty users)" },
        ],
      },
      {
        key: "social",
        labelFallback: "Social & Engagement",
        items: [
          { key: "so1", fallback: "Recently Viewed Products" },
          { key: "so2", fallback: "Product Reviews & Ratings" },
        ],
      },
    ],
  },
  {
    id: "seller",
    eventKey: "1",
    roleName: "Seller",
    roleIndex: 4,
    icon: FaStore,
    accentColor: "#00a4d8",
    priceMonth: 34,
    priceYear: 300,
    titleKey: "beSeller",
    taglineKey: "sellerTagline",
    taglineFallback: "Sell & Grow",
    descriptionKey: "sellerDescription",
    descriptionFallback: "Build your storefront and sell products directly to customers.",
    // "Ideal for" target profiles are NOT listed per-plan here -- RoleTargetProfiles.jsx sources
    // them from userStep/targetProfiles.js's own TARGET_PROFILES[].roles (single source of truth,
    // so a profile shared across roles, e.g. Artisan, is declared once, not duplicated per plan).
    available: true,
    prixAvailable: false,
    freeTrialMonths: 12,
    includesPlanId: "royalty",
    privilegeGroups: [
      {
        key: "sellCenter",
        labelFallback: "Sell Center",
        featureKey: "sellProduct",
        items: [
          { key: "p9", fallback: "Ability to create and manage your online store", route: "/myShop" },
          { key: "sc1", fallback: "Sell Center" },
          { key: "sc2", fallback: "Sell Your Product" },
          { key: "p3d", fallback: "Product management system" },
          { key: "sc3", fallback: "Edit Product" },
          { key: "sc4", fallback: "Product Status" },
        ],
      },
      {
        key: "orders",
        labelFallback: "Orders & Inventory",
        items: [
          { key: "p4d", fallback: "Inventory tracking tools" },
          { key: "or1", fallback: "Orders" },
          { key: "or2", fallback: "Shipping" },
        ],
      },
      {
        // Renamed from "Dropshipping In" for clarity/disambiguation against the new
        // "External Dropshipping" group below (ticket: distinguish Elakay-internal
        // Provider->Seller dropshipping from external-supplier dropshipping) -- key/route/
        // featureKey unchanged, display label only.
        key: "dropshippingIn",
        labelFallback: "Elakay Provider Dropshipping",
        featureKey: "dropshippingIn",
        items: [
          { key: "p10", fallback: "Access to Provider products for dropshipping", route: "/seller/dropshipping-in" },
          { key: "ds1", fallback: "Discover Providers", route: "/seller/providers" },
          { key: "ds2", fallback: "Provider Matrix" },
          { key: "ds3", fallback: "View All Provider Products", route: "/seller/provider-catalog" },
          { key: "ds4", fallback: "Provider Product Preview" },
          { key: "ds5", fallback: "Add Provider Product to My Shop" },
          { key: "p11", fallback: "Manage and edit your dropshipping listings", route: "/seller/provider-products" },
          { key: "ds6", fallback: "Multi-Provider Support" },
        ],
      },
      {
        // Distinct from the group above: sourcing products from suppliers OUTSIDE Elakay
        // (CJDropshipping/Printful/Printify/Syncee/Inventory Source -- server_elakay's own
        // productSource.js helper calls this exact concept "external Dropshipping", as opposed
        // to Provider-internal dropshipping). This is a real, already-live Seller feature
        // (Sell Center's own "Dropshipping Out" card links here) that plans.js simply never
        // listed before -- featureKey "dropshippingOut" and the route already exist, so this
        // reuses the real status/destination rather than inventing either.
        key: "dropshippingOut",
        labelFallback: "External Dropshipping",
        featureKey: "dropshippingOut",
        items: [
          { key: "do1", fallback: "Import products from external suppliers", route: "/dropshipping/platforms" },
        ],
      },
      {
        key: "providerDiscovery",
        labelFallback: "Provider Discovery",
        items: [
          { key: "pd1", fallback: "Provider Views" },
          { key: "pd2", fallback: "Provider Likes" },
          { key: "pd3", fallback: "Favorite / Liked Providers" },
          { key: "pd4", fallback: "Provider Product Views" },
        ],
      },
      {
        key: "sync",
        labelFallback: "Synchronization",
        items: [
          { key: "sy1", fallback: "Resynchronization" },
          { key: "sy2", fallback: "Manual Synchronization" },
          { key: "sy3", fallback: "Provider Image Synchronization" },
          { key: "sy4", fallback: "Provider Price Synchronization" },
          { key: "sy5", fallback: "Provider Stock Synchronization" },
          { key: "sy6", fallback: "Provider Filters & Variants Synchronization" },
          { key: "sy7", fallback: "Made In Synchronization" },
        ],
      },
      {
        key: "wholesale",
        labelFallback: "Wholesale",
        featureKey: "wholesale",
        items: [
          { key: "p12", fallback: "Purchase products in bulk as a big seller" },
          { key: "p13", fallback: "Negotiate large-quantity orders with Providers" },
          { key: "wh1", fallback: "Minimum Quantity" },
          { key: "wh2", fallback: "Tier Pricing" },
          { key: "wh3", fallback: "Quantity Discounts" },
        ],
      },
      {
        key: "marketing",
        labelFallback: "Marketing",
        items: [
          { key: "p2d", fallback: "Advanced seller dashboard" },
          { key: "p5d", fallback: "Sales analytics and performance reports" },
          { key: "p6d", fallback: "Customer insights and behavior analytics" },
          { key: "p7d", fallback: "Marketing and promotion tools" },
          { key: "p8d", fallback: "Access to featured listings (boost products)" },
          { key: "ma1", fallback: "Email Marketing", route: "/elakay/email-marketing-dashboard" },
          { key: "ma2", fallback: "Product Sharing" },
        ],
      },
    ],
  },
  {
    id: "provider",
    eventKey: "2",
    roleName: "Provider",
    roleIndex: 3,
    icon: FaIndustry,
    accentColor: "#0c85b5",
    priceMonth: 66,
    priceYear: 600,
    titleKey: "beProvider",
    taglineKey: "providerTagline",
    taglineFallback: "Supply & Produce",
    // Provider is its own product-creator/producer/supplier role -- it is NOT "Seller plus
    // extras". It inherits the base USER ROYALE (Royalty) account benefits, same as Seller does,
    // never Seller's own benefits (Seller and Provider are peer roles server-side, see
    // server_elakay/elakay_services/roleSwitchContext.js's SILOED_PEER_ROLES). Provider's own
    // wholesale/dropshipping/fulfillment capabilities below are defined independently of Seller's.
    descriptionKey: "providerDescription",
    descriptionFallback: "Create, produce, manage and supply your own products to customers and Sellers.",
    // "Ideal for" target profiles are NOT listed per-plan here -- see the matching comment on the
    // Seller plan above. Provider's full profile list (Artisan, Manufacturer, Exporter, etc.) lives
    // in userStep/targetProfiles.js, tagged roles: ["provider"] (or ["seller","provider"] when shared).
    available: true,
    prixAvailable: false,
    freeTrialMonths: 12,
    includesPlanId: "royalty",
    // Customer-facing redesign (ticket §4/§5): DISPLAY labels only -- every `key`/`route`/
    // `featureKey` below is unchanged from before, so nothing downstream (routes, Animation
    // Settings lookups, planPrivileges translation keys) breaks. The old "providerCenter" +
    // "productManagement" groups are merged into one "Product Creation & Management" category
    // (matches the ticket's own category grouping); "pc1" is dropped since it was a plain label
    // chip that only repeated the old group's own name and carried no route.
    privilegeGroups: [
      {
        key: "productCreation",
        labelFallback: "Product Creation & Management",
        featureKey: "providerProducerIn",
        items: [
          { key: "p2", fallback: "Ability to produce and manufacture products" },
          { key: "pc2", fallback: "Product Portfolio Management", route: "/provider/products" },
          { key: "pc3", fallback: "Create & Manage Products", route: "/provider/products/add" },
          { key: "pc4", fallback: "Product Availability & Publishing" },
          { key: "pc5", fallback: "Sales & Profit Simulator" },
          { key: "p4d", fallback: "Create your own brand and product lines" },
          { key: "pm1", fallback: "Product Categories & Organization" },
          { key: "pm2", fallback: "Product Options & Variants" },
          { key: "pm3", fallback: "Product Media Gallery" },
          { key: "pm4", fallback: "Product Origin & Manufacturing" },
          { key: "pm5", fallback: "Marketplace Visibility" },
        ],
      },
      {
        key: "wholesale",
        labelFallback: "Wholesale & Distribution",
        featureKey: "wholesale",
        items: [
          { key: "p3", fallback: "Sell products in bulk (wholesale system)" },
          { key: "p9", fallback: "Bulk pricing control and distribution system" },
          { key: "wh1", fallback: "Minimum Quantity" },
          { key: "wh2", fallback: "Tier Prices" },
          { key: "wh3", fallback: "Seller Bulk Orders", route: "/provider/orders" },
        ],
      },
      {
        key: "dropshippingIn",
        labelFallback: "Dropshipping Distribution",
        featureKey: "dropshippingIn",
        items: [
          { key: "p5", fallback: "Provide your products to Sellers for dropshipping" },
          { key: "p7", fallback: "Seller Distribution Network", route: "/provider/connected-sellers" },
          { key: "ds1", fallback: "Provider → Seller Product Relationship" },
          { key: "ds2", fallback: "Multiple Seller Relationships" },
          { key: "ds3", fallback: "Provider Price Source" },
          { key: "ds4", fallback: "Stock Source" },
          { key: "ds5", fallback: "Resynchronization" },
        ],
      },
      {
        key: "fulfillment",
        labelFallback: "Fulfillment & Logistics",
        featureKey: "providerFulfillment",
        items: [
          { key: "p6", fallback: "Automatically handle shipping for Seller orders" },
          { key: "p8", fallback: "Supply chain and logistics management tools" },
          { key: "fl1", fallback: "Inventory & Availability", route: "/provider/inventory" },
          { key: "fl2", fallback: "Order Management", route: "/provider/orders" },
          { key: "fl3", fallback: "Shipping Configuration" },
          { key: "fl4", fallback: "Tracking" },
        ],
      },
      {
        key: "businessTools",
        labelFallback: "Business & Marketplace Tools",
        items: [
          { key: "en1", fallback: "Provider Views" },
          { key: "en2", fallback: "Provider Likes" },
          { key: "en3", fallback: "Product Views" },
          { key: "en4", fallback: "Seller Interest & Activity" },
          { key: "p10", fallback: "Advanced production analytics" },
          { key: "p11", fallback: "Access to large-scale distribution network" },
          { key: "p12", fallback: "Priority placement in marketplace (high visibility)" },
          { key: "p13", fallback: "Partnership opportunities with other Providers and Sellers" },
        ],
      },
    ],
  },
];
